import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Check, X } from "lucide-react";

// =============== INTAKE LIST ===============
export function AdminIntake() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/intake").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="admin-intake-page">
      <div className="eyebrow text-[var(--crest-gold)]">— Intake Forms</div>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2 mb-8">Client Intake</h1>
      <div className="space-y-4">
        {items.length === 0 && <div className="admin-card text-sm text-[var(--crest-muted)]">No submissions yet.</div>}
        {items.map((i) => (
          <div key={i._id} className="admin-card" data-testid={`intake-${i._id}`}>
            <div className="flex justify-between gap-4">
              <div>
                <h3 className="font-serif text-2xl">{i.full_name}</h3>
                <div className="text-sm text-[var(--crest-muted)]">{i.email} · {i.phone}</div>
              </div>
              <div className="text-xs text-[var(--crest-muted)] tracking-widest uppercase">{(i.created_at || "").slice(0, 10)}</div>
            </div>
            <dl className="grid md:grid-cols-2 gap-x-8 gap-y-3 mt-4 text-sm">
              <div><dt className="text-[var(--crest-muted)] text-xs uppercase tracking-widest">Service</dt><dd>{i.service_type}</dd></div>
              <div><dt className="text-[var(--crest-muted)] text-xs uppercase tracking-widest">Budget</dt><dd>{i.budget_range}</dd></div>
              <div><dt className="text-[var(--crest-muted)] text-xs uppercase tracking-widest">Organization</dt><dd>{i.organization || "—"}</dd></div>
              <div><dt className="text-[var(--crest-muted)] text-xs uppercase tracking-widest">Timeline</dt><dd>{i.timeline || "—"}</dd></div>
              <div className="md:col-span-2"><dt className="text-[var(--crest-muted)] text-xs uppercase tracking-widest">Goals</dt><dd>{i.project_goals}</dd></div>
              {i.additional_notes && <div className="md:col-span-2"><dt className="text-[var(--crest-muted)] text-xs uppercase tracking-widest">Notes</dt><dd>{i.additional_notes}</dd></div>}
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============== CONTACT MESSAGES ===============
export function AdminContact() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/contact").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="admin-contact-page">
      <div className="eyebrow text-[var(--crest-gold)]">— Contact</div>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2 mb-8">Inbox</h1>
      <div className="space-y-4">
        {items.length === 0 && <div className="admin-card text-sm text-[var(--crest-muted)]">No messages yet.</div>}
        {items.map((c) => (
          <div key={c._id} className="admin-card" data-testid={`contact-${c._id}`}>
            <div className="flex justify-between">
              <div>
                <h3 className="font-serif text-2xl">{c.name}</h3>
                <div className="text-sm text-[var(--crest-muted)]">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
              </div>
              <div className="text-xs text-[var(--crest-muted)] tracking-widest uppercase">{(c.created_at || "").slice(0, 10)}</div>
            </div>
            <div className="mt-3 text-sm">{c.service_type && <span className="pill mr-2">{c.service_type}</span>}{c.budget && <span className="pill">{c.budget}</span>}</div>
            <p className="mt-4 text-sm leading-relaxed">{c.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============== NEWSLETTER ===============
export function AdminNewsletter() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/newsletter").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const exportCsv = () => {
    const rows = [["email", "name", "created_at"], ...items.map((i) => [i.email, i.name || "", i.created_at])];
    const csv = rows.map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "newsletter-subscribers.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div data-testid="admin-newsletter-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="eyebrow text-[var(--crest-gold)]">— Newsletter</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2">Subscribers ({items.length})</h1>
        </div>
        <button onClick={exportCsv} className="btn-secondary" data-testid="newsletter-export">Export CSV</button>
      </div>
      <div className="admin-card">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[var(--crest-muted)] border-b border-black/10"><th className="py-3 px-2">Email</th><th className="py-3 px-2">Name</th><th className="py-3 px-2">Subscribed</th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={3} className="py-10 text-center text-[var(--crest-muted)]">No subscribers yet.</td></tr>}
            {items.map((s) => (
              <tr key={s._id} className="border-b border-black/5">
                <td className="py-3 px-2">{s.email}</td>
                <td className="py-3 px-2">{s.name || "—"}</td>
                <td className="py-3 px-2 text-[var(--crest-muted)]">{(s.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============== TESTIMONIALS ===============
export function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const update = async (id, patch) => { await api.patch(`/admin/testimonials/${id}`, patch); load(); };
  const remove = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/admin/testimonials/${id}`); load(); } };
  return (
    <div data-testid="admin-testimonials-page">
      <div className="eyebrow text-[var(--crest-gold)]">— Testimonials</div>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2 mb-8">Reviews</h1>
      <div className="grid md:grid-cols-2 gap-4">
        {items.length === 0 && <div className="admin-card text-sm text-[var(--crest-muted)] md:col-span-2">No testimonials.</div>}
        {items.map((t) => (
          <div key={t._id} className={`admin-card ${t.published ? "" : "opacity-75"}`} data-testid={`testimonial-${t._id}`}>
            <div className="flex justify-between gap-3">
              <div>
                <div className="font-medium">{t.client_name}</div>
                <div className="text-xs text-[var(--crest-muted)]">{t.client_title}{t.organization ? ` · ${t.organization}` : ""}</div>
              </div>
              <div className="text-xs text-[var(--crest-muted)]">★ {t.rating}</div>
            </div>
            <p className="font-serif text-lg mt-3">"{t.quote}"</p>
            <div className="flex items-center gap-2 mt-4">
              <button onClick={() => update(t._id, { published: !t.published })} className={t.published ? "btn-secondary" : "btn-primary"} data-testid={`testimonial-toggle-${t._id}`}>
                {t.published ? <><X size={14} /> Unpublish</> : <><Check size={14} /> Publish</>}
              </button>
              <button onClick={() => remove(t._id)} className="text-[var(--crest-muted)] hover:text-red-600 p-2"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============== PORTFOLIO ADMIN ===============
const PORTFOLIO_CATEGORIES = ["branding", "campaign", "creative_direction", "event", "production", "conference"];
export function AdminPortfolio() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title: "", client: "", category: PORTFOLIO_CATEGORIES[0], year: "", description: "", cover_image_url: "", featured: false, published: true, sort_order: 0 });
  const load = () => api.get("/portfolio").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/portfolio", form); toast.success("Added"); setShow(false); load(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/admin/portfolio/${id}`); load(); } };
  return (
    <div data-testid="admin-portfolio-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="eyebrow text-[var(--crest-gold)]">— Portfolio</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2">Portfolio Items</h1>
        </div>
        <button onClick={() => setShow((v) => !v)} className="btn-primary" data-testid="portfolio-new"><Plus size={14} /> New Item</button>
      </div>
      {show && (
        <form onSubmit={create} className="admin-card grid md:grid-cols-2 gap-x-8 gap-y-2 mb-8">
          <input className="crest-input md:col-span-2" placeholder="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <input className="crest-input" placeholder="Client" required value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} />
          <select className="crest-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {PORTFOLIO_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input className="crest-input" placeholder="Year" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
          <input className="crest-input" type="number" placeholder="Sort order" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
          <input className="crest-input md:col-span-2" placeholder="Cover image URL" required value={form.cover_image_url} onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })} />
          <textarea className="crest-input md:col-span-2" placeholder="Description" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <button className="btn-primary md:col-span-2">Save</button>
        </form>
      )}
      <div className="grid md:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p._id} className="admin-card">
            <div className="aspect-[4/3] overflow-hidden mb-3"><img src={p.cover_image_url} alt="" className="w-full h-full object-cover" /></div>
            <div className="font-serif text-xl">{p.title}</div>
            <div className="text-xs text-[var(--crest-muted)] mt-1">{p.client} · {p.category}</div>
            <button onClick={() => remove(p._id)} className="mt-3 text-xs text-red-600 flex items-center gap-1"><Trash2 size={12} /> Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}
